import { Settings, Globe, Eye, EyeOff, Zap, ZapOff } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/components/LanguageProvider';

interface TickerSettingsData {
  hideLearned: boolean;
  reducedMotion: boolean;
}

const SETTINGS_KEY = 'cr_ticker_settings';

function loadSettings(): TickerSettingsData {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { hideLearned: false, reducedMotion: false };
}

function saveSettings(s: TickerSettingsData) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
}

interface TickerSettingsProps {
  onSettingsChange: (s: TickerSettingsData) => void;
}

export function TickerSettings({ onSettingsChange }: TickerSettingsProps) {
  const { language, setLanguage } = useLanguage();
  const [settings, setSettings] = useState<TickerSettingsData>(loadSettings);
  const isFr = language === 'fr';

  useEffect(() => {
    saveSettings(settings);
    onSettingsChange(settings);
  }, [settings, onSettingsChange]);

  const update = (patch: Partial<TickerSettingsData>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="p-1 rounded-full hover:bg-accent/20 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={isFr ? 'Paramètres du ticker' : 'Ticker settings'}
        >
          <Settings className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-56 bg-background/95 backdrop-blur-sm border-border/50 p-3"
      >
        <h4 className="text-xs font-semibold text-foreground mb-3 tracking-wide">
          {isFr ? 'Paramètres' : 'Settings'}
        </h4>

        <div className="space-y-3">
          {/* Language */}
          <div className="flex items-center justify-between">
            <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
              <Globe className="w-3 h-3" />
              {isFr ? 'Langue' : 'Language'}
            </Label>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setLanguage('fr')}
                className={`text-[10px] px-1.5 py-0.5 rounded ${language === 'fr' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                FR
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`text-[10px] px-1.5 py-0.5 rounded ${language === 'en' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                EN
              </button>
            </div>
          </div>

          {/* Hide learned */}
          <div className="flex items-center justify-between">
            <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
              {settings.hideLearned ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {isFr ? 'Masquer appris' : 'Hide learned'}
            </Label>
            <Switch
              checked={settings.hideLearned}
              onCheckedChange={(v) => update({ hideLearned: v })}
              className="scale-75"
            />
          </div>

          {/* Reduced motion */}
          <div className="flex items-center justify-between">
            <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
              {settings.reducedMotion ? <ZapOff className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
              {isFr ? 'Réduire mouvement' : 'Reduce motion'}
            </Label>
            <Switch
              checked={settings.reducedMotion}
              onCheckedChange={(v) => update({ reducedMotion: v })}
              className="scale-75"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export type { TickerSettingsData };
